import { of, switchMap, from, retry, Subject, ReplaySubject, share, startWith, tap, interval, merge, debounceTime, filter, EMPTY, catchError } from 'rxjs';

import { authorizedFetch, readJson, postText, postJson, deleteEntity } from  './http.js';

/**
 * collectionsReloadTrigger$ emits undefined when a collection was
 * added or removed.
 */
let collectionsReloadTrigger$ = new Subject();

/**
 * collectionChangedTrigger$ emits the collection ID when a document or section
 * in the collection changed.
 */
let collectionChangedTrigger$ = new Subject();

export let collections$ = collectionsReloadTrigger$.pipe(
    startWith(undefined),
    switchMap(() => from(authorizedFetch('/api/collections'))),
    readJson(),
    share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: true,
        resetOnRefCountZero: true,
        resetOnError: true,
    }),
);

export function addCollection(collection) {
    return postJson('/api/collections', collection).pipe(
        tap(() => collectionsReloadTrigger$.next()),
    );
}

export function deleteCollection(colId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/collection/${colId}`, {
            method: 'DELETE',
        }))),
        readJson(),
    );
}

export function truncateCollection(colId) {
    return postJson(`/api/collection/${colId}/truncate`, {}).pipe(
        tap(() => collectionChangedTrigger$.next(colId)),
    );
}

export function updateCollection(colId, col) {
    return postJson(`/api/collection/${colId}/properties`, col).pipe(
        tap(() => {
            collectionsReloadTrigger$.next();
            collectionChangedTrigger$.next(colId);
        }),
    );
}

export function getCollectionMetadata(colId) {
    return merge(
        of(undefined),
        collectionChangedTrigger$.pipe(
            filter(changedColId => changedColId === colId),
            debounceTime(300),
        ),
        interval(30*1000),
    ).pipe(
        switchMap(() => of(undefined).pipe(
            switchMap(() => from(authorizedFetch(`/api/collection/${colId}/meta`))),
            readJson(),
            catchError(err => {
                console.warn(`Unable to fetch metadata for collection with ID ${colId}`, err);
                return EMPTY;
            }),
        )),
    );
}

export function getDocuments(colId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/collection/${colId}/documents`))),
        readJson(),
    );
}

export function addDocument(colId, opts) {
    return of(undefined).pipe(
        switchMap(() => {
            let { source_uri, sections } = Object.assign({}, {
                source_uri: null,
                sections: [],
            }, opts);
            return postJson(`/api/collection/${colId}/documents`, {
                source_uri,
                sections,
            });
        }),
        tap(() => collectionChangedTrigger$.next(colId)),
    );
}

export function getDocument(docId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/document/${docId}`))),
        readJson(),
    );
}

export function updateDocument(docId, doc) {
    return postJson(`/api/document/${docId}`, doc);
}

export function moveDocument(docId, targetColId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/document/${docId}/to/collection/${targetColId}`, {
            method: 'POST',
        }))),
        readJson(),
    );
}

export function importFile(colId, file, meta) {
    return of(undefined).pipe(
        switchMap(() => {
            let params = new URLSearchParams(meta.filter(m => m.value !== null).map(m => ([m.key, m.value]))).toString();
            let url = `/api/collection/${colId}/files` + (params ? '?' : '') + params;
            return from(authorizedFetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/octet-stream',
                },
                body: file,
            }));
        }),
        readJson(),
    );
}

export function getSection(secId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/section/${secId}`))),
        readJson(),
    );
}

export function saveSection(secId, text) {
    return postText(`/api/section/${secId}`, text);
}

export function findSections(context) {
    context = (context || '').trim();
    if (!context) {
        return of([]);
    }
    return postText('/api/similar-sections', context).pipe(
        retry({
            count: 3,
            delay: 3000 + 1000*Math.random(),
        }),
    );
}

export function findSectionsInCollection(colId, context) {
    context = (context || '').trim();
    if (!context) {
        return of([]);
    }
    return postText(`/api/collection/${colId}/similar-sections`, context).pipe(
        retry({
            count: 3,
            delay: 3000 + 1000*Math.random(),
        }),
    );
}

export function listCollectionApiKeys(colId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/collection/${colId}/api-keys`))),
        readJson(),
    );
}

export function createCollectionApiKey(colId, label) {
    return postJson(`/api/collection/${colId}/api-keys`, { label });
}

export function deleteCollectionApiKey(colId, keyId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/collection/${colId}/api-key/${keyId}`, {
            method: 'DELETE',
        }))),
        readJson(),
    );
}

export function createCollectionInvite(colId) {
    return postJson(`/api/collection/${colId}/invites`, {});
}

export function listCollectionInvites(colId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/collection/${colId}/invites`))),
        readJson(),
    );
}

export function deleteCollectionInvite(colId, token) {
    return deleteEntity(`/api/collection/${colId}/invite/${token}`);
}

export function acceptCollectionInvite(token) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/collection-invites/${token}/accept`, {
            method: 'POST',
        }))),
        readJson(),
    );
}

export function listCollectionMembers(colId) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/collection/${colId}/members`))),
        readJson(),
    );
}

export function removeCollectionMember(colId, memberSub) {
    return deleteEntity(`/api/collection/${colId}/member/${encodeURIComponent(memberSub)}`);
}

export function updateCollectionMembership(colId, memberUse) {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch(`/api/collection/${colId}/membership`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json; encoding=utf-8' },
            body: JSON.stringify({ member_use: memberUse }),
        }))),
        readJson(),
    );
}
